/**
 * Order Management Screen Desktop
 * 
 * Desktop version of the order management interface for managers.
 * Provides comprehensive order management with filtering, sorting, and bulk actions.
 * 
 * @module OrderManagementScreenDesktop
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  Filter,
  Download,
  RefreshCw,
  Flag,
  Search,
  FileText,
  FileSpreadsheet,
  CheckCircle,
  X,
  Trash2
} from 'lucide-react';
import { useOrders } from '../../hooks/useOrders';
import { useAuth } from '../../contexts/AuthContext';
import { OrderTable } from '../../components/manager/OrderTable';
import { Order, OrderFilters, OrderStatus, BulkOrderAction } from '../../@types/order';
import { ThemeColors, Spacing, Typography, Shadows } from '../../styles/ThemeStyles';
import { PageContainer, MainContent } from '../../styles/StyledComponents';
import { CSVExportService } from '../../services/CSVExportService';
import { PDFService } from '../../services/PDFService';
import { OrderManagementService } from '../../services/OrderManagementService';
import toast, { Toaster } from 'react-hot-toast';
import { ColumnFilter, ColumnSort } from '../../components/common/SortableFilterableTableHeader';

/**
 * Styled Components
 */
const Header = styled.div`
  margin-bottom: ${Spacing.xl};
`;

const Title = styled.h1`
  font-size: ${Typography.fontSize.xxxl};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  margin-bottom: ${Spacing.md};
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${Spacing.md};
  flex-wrap: wrap;
  margin-bottom: ${Spacing.lg};
`;

const ToolbarSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.md};
  flex-wrap: wrap;
`;

const SearchBar = styled.div`
  position: relative;
  min-width: 300px;

  svg {
    position: absolute;
    left: ${Spacing.md};
    top: 50%;
    transform: translateY(-50%);
    color: ${ThemeColors.text.secondary};
    width: 18px;
    height: 18px;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${Spacing.sm} ${Spacing.md} ${Spacing.sm} 40px;
  border: 1px solid ${ThemeColors.border.main};
  border-radius: 8px;
  font-size: ${Typography.fontSize.sm};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
    box-shadow: 0 0 0 3px ${ThemeColors.primary.light};
  }

  &::placeholder {
    color: ${ThemeColors.text.secondary};
  }
`;

const FilterButton = styled.button<{ active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.sm} ${Spacing.md};
  border: 1px solid ${props => props.active ? ThemeColors.primary.main : ThemeColors.border.main};
  background: ${props => props.active ? ThemeColors.primary.light : 'white'};
  color: ${props => props.active ? ThemeColors.primary.main : ThemeColors.text.primary};
  border-radius: 8px;
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    background: ${ThemeColors.primary.light};
    border-color: ${ThemeColors.primary.main};
    color: ${ThemeColors.primary.main};
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'success' | 'danger' }>`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.sm} ${Spacing.md};
  border: none;
  background: ${props => {
    switch (props.variant) {
      case 'primary': return ThemeColors.primary.main;
      case 'success': return ThemeColors.success.main;
      case 'danger': return ThemeColors.error.main;
      default: return 'white';
    }
  }};
  color: ${props => props.variant === 'secondary' ? ThemeColors.text.primary : 'white'};
  border: ${props => props.variant === 'secondary' ? `1px solid ${ThemeColors.border.main}` : 'none'};
  border-radius: 8px;
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const StatusFilterGroup = styled.div`
  display: flex;
  gap: ${Spacing.xs};
  background: ${ThemeColors.background.secondary};
  padding: ${Spacing.xs};
  border-radius: 8px;
`;

const StatusFilter = styled.button<{ active?: boolean; status: OrderStatus | 'all' }>`
  padding: ${Spacing.xs} ${Spacing.sm};
  border: none;
  background: ${props => props.active ? {
    'pending': ThemeColors.warning.main,
    'approved': ThemeColors.success.main,
    'processing': ThemeColors.info.main,
    'completed': ThemeColors.success.dark,
    'cancelled': ThemeColors.error.main,
    'submitted': ThemeColors.warning.light,
    'all': ThemeColors.primary.main
  }[props.status] || 'transparent' : 'transparent'};
  color: ${props => props.active ? 'white' : ThemeColors.text.secondary};
  border-radius: 6px;
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;
  text-transform: capitalize;

  &:hover {
    background: ${props => props.active ? undefined : ThemeColors.background.tertiary};
  }
`;

const BulkActionsBar = styled.div<{ visible: boolean }>`
  display: ${props => props.visible ? 'flex' : 'none'};
  align-items: center;
  justify-content: space-between;
  padding: ${Spacing.md};
  background: ${ThemeColors.primary.light};
  border-radius: 8px;
  margin-bottom: ${Spacing.lg};
`;

const SelectedCount = styled.span`
  font-weight: ${Typography.fontWeight.semibold};
  color: ${ThemeColors.primary.main};
`;

const ErrorMessage = styled.div`
  padding: ${Spacing.md};
  background: ${ThemeColors.error.light};
  color: ${ThemeColors.error.dark};
  border-radius: 8px;
  margin-bottom: ${Spacing.lg};
`;

const ModalOverlay = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: ${Spacing.xl};
  max-width: 500px;
  width: 90%;
  box-shadow: ${Shadows.lg};
`;

const ModalTitle = styled.h2`
  font-size: ${Typography.fontSize.xl};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  margin-bottom: ${Spacing.md};
`;

const ModalText = styled.p`
  font-size: ${Typography.fontSize.md};
  color: ${ThemeColors.text.secondary};
  margin-bottom: ${Spacing.lg};
`;

const ModalTextarea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: ${Spacing.md};
  border: 1px solid ${ThemeColors.border.main};
  border-radius: 8px;
  font-size: ${Typography.fontSize.sm};
  font-family: inherit;
  resize: vertical;
  margin-bottom: ${Spacing.lg};
  
  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
    box-shadow: 0 0 0 3px ${ThemeColors.primary.light};
  }
`;

const ModalButtons = styled.div`
  display: flex;
  gap: ${Spacing.md};
  justify-content: flex-end;
`;

/**
 * Order Management Screen Desktop Component
 * 
 * Provides comprehensive order management interface including:
 * - Filtering and searching
 * - Bulk actions
 * - Order approval/rejection
 * - Status management
 */
export const OrderManagementScreenDesktop: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { t } = useTranslation();
  
  // Use orders hook
  const {
    orders,
    isLoading,
    isLoadingMore,
    error,
    fetchAllOrders,
    loadMoreOrders,
    canLoadMore,
    flagOrder,
    unflagOrder,
    clearError
  } = useOrders();

  // Local state
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<OrderStatus | 'all'>('all');
  const [selectedOrderIds, setSelectedOrderIds] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [columnFilters, setColumnFilters] = useState<ColumnFilter[]>([]);
  // Default the list to "Date, newest first" so recent orders are always on top.
  const [columnSort, setColumnSort] = useState<ColumnSort | null>({
    column: 'orderDate',
    direction: 'desc'
  });
  
  // Debounce search input to avoid excessive queries
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  useEffect(() => {
    // Debounce search - wait 400ms after user stops typing
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 400);
    
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  /**
   * Load orders on component mount and when filters change
   * Uses debounced search term to avoid excessive queries
   */
  useEffect(() => {
    const filters: OrderFilters = {};
    
    // Apply status filter from URL or state
    const statusParam = searchParams.get('status') as OrderStatus;
    const activeStatus = statusParam || selectedStatus;
    
    if (activeStatus !== 'all') {
      filters.status = activeStatus;
    }

    // Apply debounced search term
    if (debouncedSearchTerm) {
      filters.searchTerm = debouncedSearchTerm;
    }

    // Apply column filters - map column keys to OrderFilters
    // Support both single values and arrays (multi-select)
    columnFilters.forEach(filter => {
      if (filter.value) {
        const values = Array.isArray(filter.value) ? filter.value : [filter.value];
        const filterValue = values.length === 1 ? values[0] : values;
        
        switch (filter.column) {
          case 'salespersonNo':
            filters.salespersonNo = filterValue as string | string[];
            break;
          case 'city':
            filters.city = filterValue as string | string[];
            break;
          case 'province':
            filters.province = filterValue as string | string[];
            break;
          case 'orderType':
            filters.orderType = filterValue as string | string[];
            break;
          case 'status':
            // Status is handled separately via the status buttons
            break;
        }
      }
    });
    
    // Apply sorting from columnSort if available
    const sortField = columnSort?.column === 'orderDate' ? 'orderDate' :
                      columnSort?.column === 'totalAmount' ? 'totalAmount' :
                      columnSort?.column === 'storeName' ? 'storeName' :
                      'orderDate';
    const sortDirection = columnSort?.direction === 'asc' ? 'asc' : 'desc';

    fetchAllOrders(filters, { field: sortField as any, direction: sortDirection });
  }, [selectedStatus, debouncedSearchTerm, columnFilters, columnSort, searchParams, fetchAllOrders]);

  /**
   * Handle order view — open report in a new tab so the list stays open
   */
  const handleViewOrder = useCallback((order: Order) => {
    const path = `/manager/order-details/${encodeURIComponent(order.storeId)}/${encodeURIComponent(order.orderId)}`;
    window.open(`${window.location.origin}${path}`, '_blank', 'noopener,noreferrer');
  }, []);

  /**
   * Handle PDF download for single order
   */
  const handleDownloadPDF = useCallback(async (order: Order) => {
    try {
      const pdfBlob = await PDFService.generateOrderPDF({
        orderData: order,
        includeItems: true,
        includeCustomerInfo: true
      });
      
      const filename = `order_${order.orderId}_${new Date().toISOString().split('T')[0]}.pdf`;
      PDFService.downloadPDF(pdfBlob, filename);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    }
  }, []);

  /**
   * Handle CSV download for single order
   */
  const handleDownloadCSV = useCallback(async (order: Order) => {
    try {
      await CSVExportService.exportOrderToCSV(order, {
        includeItems: true,
        includeCustomerInfo: true
      });
    } catch (error) {
      console.error('Error exporting CSV:', error);
      alert('Failed to export CSV. Please try again.');
    }
  }, []);

  /**
   * Handle refresh
   */
  const handleRefresh = useCallback(() => {
    const filters: OrderFilters = {};
    if (selectedStatus !== 'all') {
      filters.status = selectedStatus;
    }
    if (debouncedSearchTerm) {
      filters.searchTerm = debouncedSearchTerm;
    }
    
    // Apply column filters
    columnFilters.forEach(filter => {
      if (filter.value) {
        const values = Array.isArray(filter.value) ? filter.value : [filter.value];
        const filterValue = values.length === 1 ? values[0] : values;
        
        switch (filter.column) {
          case 'salespersonNo':
            filters.salespersonNo = filterValue as string | string[];
            break;
          case 'city':
            filters.city = filterValue as string | string[];
            break;
          case 'province':
            filters.province = filterValue as string | string[];
            break;
          case 'orderType':
            filters.orderType = filterValue as string | string[];
            break;
        }
      }
    });
    
    const sortField = columnSort?.column === 'orderDate' ? 'orderDate' :
                      columnSort?.column === 'totalAmount' ? 'totalAmount' :
                      columnSort?.column === 'storeName' ? 'storeName' :
                      'orderDate';
    const sortDirection = columnSort?.direction === 'asc' ? 'asc' : 'desc';
    
    fetchAllOrders(filters, { field: sortField as any, direction: sortDirection });
  }, [selectedStatus, debouncedSearchTerm, columnFilters, columnSort, fetchAllOrders]);

  // State for flag modal
  const [flagModalOpen, setFlagModalOpen] = useState(false);
  const [flagReason, setFlagReason] = useState('');
  const [orderToFlag, setOrderToFlag] = useState<Order | null>(null);

  /**
   * Handle order flag toggle
   */
  const handleFlag = useCallback(async (order: Order) => {
    if (!user?.id) return;
    
    if (order.flaggedForReview) {
      await unflagOrder(order.storeId, order.orderId, user.id);
    } else {
      // Open modal for flagging
      setOrderToFlag(order);
      setFlagReason('');
      setFlagModalOpen(true);
    }
  }, [unflagOrder, user]);

  /**
   * Handle flag confirmation
   */
  const handleFlagConfirm = useCallback(async () => {
    if (!user?.id || !orderToFlag || !flagReason.trim()) {
      toast.error('Please provide a reason for flagging');
      return;
    }
    
    try {
      await flagOrder(orderToFlag.storeId, orderToFlag.orderId, user.id, flagReason.trim());
      setFlagModalOpen(false);
      setFlagReason('');
      setOrderToFlag(null);
      toast.success('Order flagged for review');
    } catch (error) {
      console.error('Error flagging order:', error);
      toast.error('Failed to flag order');
    }
  }, [flagOrder, user, orderToFlag, flagReason]);

  /**
   * Handle flag modal close
   */
  const handleFlagModalClose = useCallback(() => {
    setFlagModalOpen(false);
    setFlagReason('');
    setOrderToFlag(null);
  }, []);

  /**
   * Handle single order approval
   */
  const handleApproveOrder = useCallback(async (order: Order) => {
    if (!user?.id) return;
    const managerDisplayName = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.username || user.email || user.id;
    try {
      const result = await OrderManagementService.approveOrder(
        order.storeId,
        order.orderId,
        user.id,
        undefined,
        managerDisplayName
      );
      
      if (result.success) {
        toast.success('Order approved successfully');
        handleRefresh();
      } else {
        toast.error(result.error || 'Failed to approve order');
      }
    } catch (error: any) {
      console.error('Error approving order:', error);
      toast.error('Failed to approve order');
    }
  }, [user, handleRefresh]);

  /**
   * Handle bulk order approval
   */
  const handleBulkApprove = useCallback(async () => {
    if (!user?.id || selectedOrderIds.length === 0) return;
    
    if (!window.confirm(`Are you sure you want to approve ${selectedOrderIds.length} order(s)?`)) {
      return;
    }
    
    try {
      const managerDisplayName = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.username || user.email || user.id;
      const bulkAction: BulkOrderAction = {
        orderIds: selectedOrderIds,
        action: 'approve',
        managerDisplayName
      };
      const results = await OrderManagementService.performBulkAction(bulkAction, user.id);
      
      const successCount = results.filter(r => r.success).length;
      const failCount = results.filter(r => !r.success).length;
      
      if (successCount > 0) {
        toast.success(`Successfully approved ${successCount} order(s)`);
      }
      if (failCount > 0) {
        toast.error(`Failed to approve ${failCount} order(s)`);
      }
      
      setSelectedOrderIds([]);
      handleRefresh();
    } catch (error: any) {
      console.error('Error bulk approving orders:', error);
      toast.error('Failed to approve orders');
    }
  }, [user, selectedOrderIds, handleRefresh]);

  /**
   * Handle single order deletion
   */
  const handleDeleteOrder = useCallback(async (order: Order) => {
    if (!user?.id) return;
    
    if (!window.confirm(`Are you sure you want to delete order ${order.orderId}? This action cannot be undone.`)) {
      return;
    }
    
    try {
      const result = await OrderManagementService.deleteOrder(
        order.storeId,
        order.orderId,
        user.id
      );
      
      if (result.success) {
        toast.success('Order deleted successfully');
        handleRefresh();
      } else {
        toast.error(result.error || 'Failed to delete order');
      }
    } catch (error: any) {
      console.error('Error deleting order:', error);
      toast.error('Failed to delete order');
    }
  }, [user, handleRefresh]);

  /**
   * Handle bulk order deletion
   */
  const handleBulkDelete = useCallback(async () => {
    if (!user?.id || selectedOrderIds.length === 0) return;
    
    if (!window.confirm(`Are you sure you want to delete ${selectedOrderIds.length} order(s)? This action cannot be undone.`)) {
      return;
    }
    
    try {
      const bulkAction: BulkOrderAction = {
        orderIds: selectedOrderIds,
        action: 'delete'
      };
      
      const results = await OrderManagementService.performBulkAction(bulkAction, user.id);
      
      const successCount = results.filter(r => r.success).length;
      const failCount = results.filter(r => !r.success).length;
      
      if (successCount > 0) {
        toast.success(`Successfully deleted ${successCount} order(s)`);
      }
      if (failCount > 0) {
        toast.error(`Failed to delete ${failCount} order(s)`);
      }
      
      setSelectedOrderIds([]);
      handleRefresh();
    } catch (error: any) {
      console.error('Error bulk deleting orders:', error);
      toast.error('Failed to delete orders');
    }
  }, [user, selectedOrderIds, handleRefresh]);

  /**
   * Handle send order to customer service
   */
  const handleSendToCustomerService = useCallback(async (order: Order) => {
    if (!user?.id) return;

    try {
      const result = await OrderManagementService.sendApprovedOrderEmailToCustomerService(
        order.storeId,
        order.orderId,
        user.id
      );

      if (result.success) {
        toast.success('Sent to the portal and Customer Service successfully');
        handleRefresh();
      } else {
        toast.error(result.error || 'Failed to send order to customer service');
      }
    } catch (error: any) {
      console.error('Error sending order to customer service:', error);
      toast.error('Failed to send order to customer service');
    }
  }, [user, handleRefresh]);

  /**
   * Handle edit order - navigate to order details with edit mode
   */
  const handleEditOrder = useCallback((order: Order) => {
    navigate(`/manager/order-details/${order.storeId}/${order.orderId}?edit=true`);
  }, [navigate]);

  /**
   * Handle bulk CSV export
   */
  const handleBulkCSVExport = useCallback(async () => {
    if (selectedOrderIds.length === 0) return;
    
    try {
      const selectedOrders = orders.filter(order => selectedOrderIds.includes(order.orderId));
      await CSVExportService.exportOrdersToCSV(selectedOrders, {
        includeItems: true,
        includeCustomerInfo: true,
        filename: `bulk_orders_export_${new Date().toISOString().split('T')[0]}.csv`
      });
      setSelectedOrderIds([]);
    } catch (error) {
      console.error('Error exporting bulk CSV:', error);
      alert('Failed to export CSV. Please try again.');
    }
  }, [selectedOrderIds, orders]);

  /**
   * Handle bulk PDF export
   */
  const handleBulkPDFExport = useCallback(async () => {
    if (selectedOrderIds.length === 0) return;
    
    try {
      const selectedOrders = orders.filter(order => selectedOrderIds.includes(order.orderId));
      
      // Export each order as a separate PDF
      for (const order of selectedOrders) {
        const pdfBlob = await PDFService.generateOrderPDF({
          orderData: order,
          includeItems: true,
          includeCustomerInfo: true
        });
        
        const filename = `order_${order.orderId}_${new Date().toISOString().split('T')[0]}.pdf`;
        PDFService.downloadPDF(pdfBlob, filename);
        
        // Small delay between downloads to prevent browser blocking
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      setSelectedOrderIds([]);
    } catch (error) {
      console.error('Error exporting bulk PDF:', error);
      alert('Failed to export PDFs. Please try again.');
    }
  }, [selectedOrderIds, orders]);

  return (
    <PageContainer>
      <Toaster position="top-right" />
      <MainContent>
        <Header>
          <Title>Order Management</Title>
        </Header>

        {/* Error Display */}
        {error && (
          <ErrorMessage>
            {error}
            <Button variant="secondary" onClick={clearError} style={{ marginLeft: Spacing.md }}>
              Dismiss
            </Button>
          </ErrorMessage>
        )}

        {/* Toolbar */}
        <Toolbar>
          <ToolbarSection>
            <SearchBar>
              <Search />
              <SearchInput
                type="text"
                placeholder="Search by order ID, store, salesperson, city, province..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </SearchBar>
            
            <StatusFilterGroup>
              <StatusFilter
                active={selectedStatus === 'all'}
                status="all"
                onClick={() => setSelectedStatus('all')}
              >
                All
              </StatusFilter>
              <StatusFilter
                active={selectedStatus === 'pending'}
                status="pending"
                onClick={() => setSelectedStatus('pending')}
              >
                Pending
              </StatusFilter>
              <StatusFilter
                active={selectedStatus === 'approved'}
                status="approved"
                onClick={() => setSelectedStatus('approved')}
              >
                Approved
              </StatusFilter>
            </StatusFilterGroup>
          </ToolbarSection>

          <ToolbarSection>
            <Button variant="secondary" onClick={handleRefresh}>
              <RefreshCw />
              Refresh
            </Button>
            <Button variant="secondary" onClick={() => CSVExportService.exportOrdersToCSV(orders, { includeItems: true, includeCustomerInfo: true })}>
              <FileSpreadsheet />
              Export All CSV
            </Button>
          </ToolbarSection>
        </Toolbar>

        {/* Bulk Actions Bar */}
        <BulkActionsBar visible={selectedOrderIds.length > 0}>
          <SelectedCount>
            {selectedOrderIds.length} order{selectedOrderIds.length !== 1 ? 's' : ''} selected
          </SelectedCount>
          <ToolbarSection>
            <Button 
              variant="success" 
              onClick={handleBulkApprove}
              disabled={selectedOrderIds.length === 0}
            >
              <CheckCircle />
              Approve Selected
            </Button>
            <Button variant="secondary" onClick={handleBulkPDFExport}>
              <FileText />
              Export PDFs
            </Button>
            <Button variant="secondary" onClick={handleBulkCSVExport}>
              <FileSpreadsheet />
              Export CSV
            </Button>
            <Button variant="secondary" onClick={() => setSelectedOrderIds([])}>
              Clear Selection
            </Button>
            <Button 
              variant="secondary" 
              onClick={handleBulkDelete}
              style={{ color: ThemeColors.error.main, borderColor: ThemeColors.error.main }}
            >
              <Trash2 size={18} />
              Delete Selected
            </Button>
          </ToolbarSection>
        </BulkActionsBar>

        {/* Orders Table */}
        <OrderTable
          orders={orders}
          isLoading={isLoading}
          onRowClick={handleViewOrder}
          onView={handleViewOrder}
          onDownloadPDF={handleDownloadPDF}
          onDownloadCSV={handleDownloadCSV}
          onFlag={handleFlag}
          onApprove={handleApproveOrder}
          onEdit={handleEditOrder}
          onDelete={handleDeleteOrder}
          onSendToCustomerService={handleSendToCustomerService}
          selectable
          selectedOrderIds={selectedOrderIds}
          onSelectionChange={setSelectedOrderIds}
          showActions
          columnFilters={columnFilters}
          onFilterChange={setColumnFilters}
          onSortChange={setColumnSort}
          currentSort={columnSort}
        />

        {canLoadMore && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: Spacing.lg,
              marginBottom: Spacing.lg
            }}
          >
            <Button
              variant="secondary"
              type="button"
              onClick={loadMoreOrders}
              disabled={isLoading || isLoadingMore}
            >
              {isLoadingMore ? 'Loading…' : `Load more (${orders.length} orders shown)`}
            </Button>
          </div>
        )}

        {/* Flag Modal */}
        <ModalOverlay isOpen={flagModalOpen} onClick={handleFlagModalClose}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: Spacing.md }}>
              <ModalTitle>Flag Order for Review</ModalTitle>
              <button
                onClick={handleFlagModalClose}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: Spacing.xs,
                  display: 'flex',
                  alignItems: 'center',
                  color: ThemeColors.text.secondary
                }}
              >
                <X size={20} />
              </button>
            </div>
            <ModalText>
              Please provide a reason for flagging this order for review:
            </ModalText>
            <ModalTextarea
              value={flagReason}
              onChange={(e) => setFlagReason(e.target.value)}
              placeholder="Enter reason for flagging..."
              autoFocus
            />
            <ModalButtons>
              <Button variant="secondary" onClick={handleFlagModalClose}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleFlagConfirm}>
                Flag Order
              </Button>
            </ModalButtons>
          </ModalContent>
        </ModalOverlay>
      </MainContent>
    </PageContainer>
  );
};

